import React, { useEffect, useState, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'

const BREAKPOINT = 768

const navItems = [
  { path: '/admin/pages',    label: 'Новини',      icon: '📰', permission: 'admin:pages' },
  { path: '/admin/archive',  label: 'Архів',       icon: '🎬', permission: 'admin:archive' },
  { path: '/admin/schedule', label: 'Планування',  icon: '📅', permission: 'admin:schedule' },
  { path: '/admin/verses',   label: 'Вірші дня',   icon: '✝',  permission: 'admin:pages' },
  { path: '/admin/users',    label: 'Користувачі', icon: '👤', permission: 'admin:users' },
  { path: '/admin/roles',    label: 'Ролі',        icon: '🔑', permission: 'admin:roles' },
]

const AdminLayout = ({ children }) => {
  const { userProfile } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [roleName, setRoleName] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth < BREAKPOINT)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // track viewport width
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < BREAKPOINT
      setIsMobile(mobile)
      if (!mobile) setDrawerOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // close drawer on navigation
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!userProfile?.roleId) { setRoleName(''); return }
    getDoc(doc(db, 'roles', userProfile.roleId))
      .then(snap => setRoleName(snap.exists() ? snap.data().name : ''))
      .catch(() => setRoleName(''))
  }, [userProfile?.roleId])

  const hasPermission = useCallback((permission) => {
    if (!userProfile) return false
    if (userProfile.permissions?.includes('admin:super')) return true
    return userProfile.permissions?.includes(permission)
  }, [userProfile])

  const handleSignOut = async () => {
    await signOut(auth)
    navigate('/admin/login')
  }

  const initials = (userProfile?.displayName || userProfile?.email || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  const sidebarContent = (
    <>
      {/* Role + User + Logout */}
      <div style={s.userBlock}>
        {roleName && <div style={s.roleTag}>{roleName}</div>}
        <div style={s.userRow}>
          <div style={s.avatar}>{initials}</div>
          <div style={s.userInfo}>
            <div style={s.userName}>{userProfile?.displayName || 'Admin'}</div>
            <div style={s.userEmail}>{userProfile?.email}</div>
          </div>
          <button onClick={handleSignOut} style={s.signOutBtn} title='Вийти'>↩</button>
        </div>
      </div>

      {/* Navigation */}
      <div style={s.sectionLabel}>Навігація</div>
      <nav style={s.nav}>
        {navItems.filter(item => hasPermission(item.permission)).map(item => {
          const active = location.pathname === item.path
          return (
            <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
              <div style={{ ...s.navItem, ...(active ? s.navItemActive : {}) }}>
                <span style={s.navIcon}>{item.icon}</span>
                <span style={s.navLabel}>{item.label}</span>
                {active && <span style={s.navPip} />}
              </div>
            </Link>
          )
        })}
      </nav>
    </>
  )

  if (isMobile) {
    return (
      <div style={{ ...s.shell, flexDirection: 'column' }}>

        {/* Mobile top bar */}
        <header style={s.topBar}>
          <button onClick={() => setDrawerOpen(o => !o)} style={s.hamburger}>
            <span style={s.hamburgerLine} />
            <span style={s.hamburgerLine} />
            <span style={s.hamburgerLine} />
          </button>
          <span style={s.topBarTitle}>Адмін</span>
          <div style={s.topBarAvatar}>{initials}</div>
        </header>

        {/* Drawer overlay */}
        {drawerOpen && (
          <div
            style={s.overlay}
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Drawer */}
        <aside style={{
          ...s.drawer,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}>
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main style={s.mainMobile}>
          {children}
        </main>

      </div>
    )
  }

  return (
    <div style={s.shell}>
      <aside style={s.sidebar}>
        {sidebarContent}
      </aside>
      <main style={s.main}>
        {children}
      </main>
    </div>
  )
}

const s = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5ede3',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },

  // ── Desktop sidebar ───────────────────────────────────────────────────────
  sidebar: {
    width: '240px',
    flexShrink: 0,
    background: '#2d1f14',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 0 24px',
  },
  main: {
    flex: 1,
    padding: '36px 40px',
    overflowY: 'auto',
    minWidth: 0,
  },

  // ── Mobile top bar ────────────────────────────────────────────────────────
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#2d1f14',
    padding: '12px 16px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    flexShrink: 0,
  },
  topBarTitle: {
    flex: 1,
    fontSize: '16px',
    fontWeight: '700',
    color: '#f0e6d8',
    letterSpacing: '0.5px',
  },
  topBarAvatar: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #c4986a, #8b6044)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
  },
  hamburger: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    flexShrink: 0,
  },
  hamburgerLine: {
    display: 'block',
    width: '22px',
    height: '2px',
    background: '#d4c0ae',
    borderRadius: '2px',
  },

  // ── Mobile drawer ─────────────────────────────────────────────────────────
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 150,
  },
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '260px',
    background: '#2d1f14',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 0 24px',
    zIndex: 200,
    transition: 'transform 0.25s ease',
    overflowY: 'auto',
  },
  mainMobile: {
    flex: 1,
    padding: '20px 16px',
    minWidth: 0,
  },

  // ── Shared sidebar internals ──────────────────────────────────────────────
  userBlock: {
    padding: '20px 16px 18px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    marginBottom: '12px',
  },
  roleTag: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#c4986a',
    marginBottom: '10px',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #c4986a, #8b6044)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#e8d8c8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userEmail: {
    fontSize: '11px',
    color: '#7a5c44',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  signOutBtn: {
    background: 'none',
    border: 'none',
    color: '#7a5c44',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    lineHeight: 1,
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#6b5040',
    padding: '0 24px',
    marginBottom: '6px',
    marginTop: '8px',
  },
  nav: {
    padding: '0 12px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '2px',
    position: 'relative',
    transition: 'background 0.15s',
  },
  navItemActive: {
    background: 'rgba(196,152,106,0.15)',
  },
  navIcon: {
    fontSize: '16px',
    width: '20px',
    textAlign: 'center',
    flexShrink: 0,
  },
  navLabel: {
    fontSize: '14px',
    color: '#d4c0ae',
    fontWeight: '500',
    flex: 1,
  },
  navPip: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#c4986a',
    flexShrink: 0,
  },
}

export default AdminLayout
